import React, { useEffect, useState } from 'react'
import { useIntl, FormatDateOptions } from 'react-intl'
import { useRouteMatch } from 'react-router'
import {
  useStandingOrders,
  PAYMENT_METHODS,
  useAccount,
  fillAndFilterProductList,
  useCustomer
} from 'msp-integrations'
import { CustomLinkComponent, DynamicTag, Spinner } from 'msp-components'
import { StandingOrdersDetailsAddress } from './StandingOrdersDetailsAddress'
import { StandingOrdersDetailsProductList } from './StandingOrdersDetailsProductList'
import StandingOrdersDetailsSummaryTotal from './StandingOrdersDetailsSummaryTotal'
import { StandingOrdersDetailsActions } from './StandingOrdersDetailsActions'
import { StandingOrdersDetailsDeliveries } from './StandingOrdersDetailsDeliveries'
import { translations } from '../AccountStandingOrders.constants'
import { AccountStandingOrdersStatus } from '../AccountStandingOrdersStatus'
import * as S from '../AccountStandingOrders.styles'
import translationsPayment from '@/components/molecules/PaymentMethodDisplay/PaymentMethodDisplay.constants'

const StandingOrdersDetails = () => {
  const { params } = useRouteMatch<{ orderNo: string }>()
  const { formatMessage, formatDate } = useIntl()
  const { getCustomerOrderProductsDetail } = useCustomer()
  const dateFormattingOpts = {
    year: 'numeric',
    day: '2-digit',
    month: '2-digit'
  } as FormatDateOptions
  const { isLoading, get } = useStandingOrders()
  const data = get(params.orderNo)
  const {
    customer: { paymentInstruments = [] }
  } = useAccount()
  const [subscriptionProducts, setSubscriptionProducts] = useState([])
  const [nextDeliveryDate, setNextDeliveryDate] = useState(
    data?.c_orderingNextShippingDate
  )

  const fetchProducts = async () => {
    const products = await fillAndFilterProductList(
      data?.customerProductListItems,
      getCustomerOrderProductsDetail
    )
    if (products && products.length > 0) {
      setSubscriptionProducts(products)
    }
  }

  const updateNextDeliveryDate = () => {
    if (data?.c_orderingDeliveryTable?.deliveries) {
      const nextDelivery = data?.c_orderingDeliveryTable?.deliveries?.find(
        (delivery: any) => delivery.status === 1
      )
      if (nextDelivery) {
        setNextDeliveryDate(nextDelivery.shippingDate)
      }
    }
  }

  useEffect(() => {
    fetchProducts()
    updateNextDeliveryDate()
  }, [data?.lastModified, data?.customerProductListItems])

  const printPaymentMethod = (paymentMethodId: string) => {
    switch (paymentMethodId) {
      case PAYMENT_METHODS.unzer_direct_debit_secured:
        return formatMessage(translationsPayment.UNZER_DIRECT_DEBIT_SECURED)
      case PAYMENT_METHODS.unzer_invoice:
        return formatMessage(translationsPayment.UNZER_INVOICE)
      case PAYMENT_METHODS.unzer_cards:
      default:
        return formatMessage(translationsPayment.UNZER_CARDS, {
          cardType: paymentInstruments.find(
            (item) =>
              item.c_registrationID === data?.c_orderingPayment?.registrationId
          )?.paymentCard?.cardType
        })
    }
  }
  return (
    <>
      <DynamicTag componentTagName='Headline' tag='h6' className='mb-4'>
        <CustomLinkComponent
          href='/account/subscriptions'
          className='font-Roboto text-14 sm:border-mediumGray hover:text-mediumGray w-full cursor-pointer underline transition duration-200 ease-in sm:mr-2 sm:w-fit sm:border-r sm:pr-2'
          uppercase={false}
          inline
        >
          {formatMessage(translations.detailsBackLink)}
        </CustomLinkComponent>
        {formatMessage(translations.detailsTitle, { orderNo: params.orderNo })}
      </DynamicTag>
      {isLoading && <Spinner />}
      {!isLoading && data && (
        <>
          <div className={S.ItemTitleLink}>
            <AccountStandingOrdersStatus status={data?.c_orderingStatus} />
          </div>
          <div className={S.DetailsShippingInfo}>
            <span className={S.DetailShippingItem}>
              {formatMessage(translations.orderStartDate, {
                startDate: formatDate(
                  data.c_orderingStartDate,
                  dateFormattingOpts
                )
              })}
            </span>
            <span className={S.DetailShippingItem}>
              {formatMessage(translations.orderDeliveryInterval, {
                weeks: data.c_orderingInterval
              })}
            </span>
            <span className={S.DetailShippingItem}>
              {formatMessage(translations.orderNextShippingDateLabel)}
              <span className='ml-1'>
                {formatDate(nextDeliveryDate, dateFormattingOpts)}
              </span>
            </span>
          </div>
          <div className={S.DetailColumnsWrapper}>
            <div className={S.Detail2ColumnsWrapper}>
              <div className={S.DetailColumn}>
                <StandingOrdersDetailsAddress
                  label={formatMessage(translations.detailShippingAddressLabel)}
                  address={data?.c_orderingShippingAddress}
                />
              </div>
              <div className={S.DetailColumn}>
                <StandingOrdersDetailsAddress
                  label={formatMessage(translations.detailBillingAddressLabel)}
                  address={data?.c_orderingBillingAddress}
                />
              </div>
            </div>
            <div className={S.Detail2ColumnsWrapper}>
              <div className={S.DetailColumn}>
                <div className={S.DetailColumnLabel}>
                  {formatMessage(translations.detailPaymentLabel)}
                </div>
                <span className={S.DetailColumnText}>
                  {printPaymentMethod(data?.c_orderingPayment?.paymentMethod)}
                </span>
              </div>
              <div className={S.DetailColumn}>
                <div className={S.DetailColumnLabel}>
                  {formatMessage(translations.detailDeliveryIntervalLabel)}
                </div>
                <span className={S.DetailColumnText}>
                  {formatMessage(translations.orderDeliveryInterval, {
                    weeks: data.c_orderingInterval
                  })}
                </span>
              </div>
            </div>
          </div>
          <StandingOrdersDetailsProductList
            productList={data?.customerProductListItems}
          />
          <StandingOrdersDetailsSummaryTotal
            order={data}
            products={subscriptionProducts}
          />
          <StandingOrdersDetailsActions
            standingOrder={data}
            products={subscriptionProducts}
          />
          <StandingOrdersDetailsDeliveries
            id={data?.id}
            deliveries={data?.c_orderingDeliveryTable?.deliveries}
            products={subscriptionProducts}
          />
          <div className='font-Robot text-14 leading-20 mb-3 mt-10 font-bold'>
            {formatMessage(translations.detailBottomText)}
          </div>
        </>
      )}
    </>
  )
}

export default StandingOrdersDetails
